import { AbstractModel, DateFormatConstants, DateUtil, isNullOrUndefined } from '@uzurv/core';
import { autoserialize, autoserializeAs, inheritSerialization } from 'cerialize';
import moment from 'moment-timezone';
import { CancellationReason } from './cancellation-reason';
import { OrganizationRider } from './organization-rider';
import { PaymentCollectionType } from './payment-collection-type';
import { RideMetadataValue } from './ride-metadata-value';
import { RideRequestLocation } from './ride-request-location';
import { RideRequestType } from './ride-request-type';
import { ServiceLevel } from './service-level';
import { TransitProgramInfo } from './transit-program-info';
import { VehicleType } from './vehicle-type';

@inheritSerialization(AbstractModel)
export class RideRequestDetail extends AbstractModel {
  @autoserialize
  itineraryId: number;

  @autoserializeAs(Date)
  itineraryPickupDate: Date;

  @autoserialize
  itineraryPartnerIdentifier: string;

  @autoserialize
  rideRequestPartnerIdentifier: string;

  @autoserializeAs(RideRequestType)
  rideRequestType: RideRequestType;

  @autoserializeAs(VehicleType)
  vehicleType: VehicleType;

  @autoserialize
  activated: boolean;

  @autoserializeAs(Date)
  activatedAt: Date;

  @autoserialize
  riderNote: string;

  @autoserializeAs(RideRequestLocation)
  pickupLocation: RideRequestLocation;

  @autoserializeAs(RideRequestLocation)
  dropoffLocation: RideRequestLocation;

  @autoserializeAs(Date)
  pickupDate: Date;

  @autoserializeAs(Date)
  dropoffDate: Date;

  @autoserialize
  dropoffDateProvided: boolean;

  @autoserialize
  distanceEstimate: number;

  @autoserialize
  durationEstimate: number;

  @autoserialize
  polylineId: number;

  @autoserialize
  polyline: string;

  @autoserialize
  driverFareEstimate: number;

  @autoserialize
  passengerCount: number;

  @autoserialize
  billablePassengerCount: number;

  @autoserialize
  accepted: boolean;

  @autoserializeAs(Date)
  acceptedDate: Date;

  @autoserializeAs(Date)
  cancelledDate: Date;

  @autoserialize
  cancelledBy: string;

  @autoserializeAs(CancellationReason)
  cancellationReason: CancellationReason;

  @autoserialize
  active: boolean;

  @autoserialize
  requestFee: number;

  @autoserializeAs(PaymentCollectionType)
  riderPaymentCollectionType: PaymentCollectionType;

  @autoserialize
  paymentMethodId: number;

  @autoserialize
  incentive: number;

  @autoserialize
  tollsEstimate: number;

  @autoserialize
  riderPaymentId: number;

  @autoserialize
  timeZone: string;

  @autoserialize
  favoriteDriversOnly: boolean;

  @autoserialize
  executeOnLyftDispatch: boolean;

  @autoserialize
  dispatchedToLyft: boolean;

  @autoserialize
  createdBy: string;

  @autoserialize
  hasInterestedDrivers: boolean;

  @autoserializeAs(OrganizationRider)
  organizationRider: OrganizationRider;

  @autoserialize
  riderPaymentAccountId: number;

  @autoserializeAs(TransitProgramInfo)
  transitProgramInfo: TransitProgramInfo;

  @autoserializeAs(Date)
  createdDate: Date;

  @autoserializeAs(Date)
  modifiedDate: Date;

  @autoserialize
  clientInvoiceEstimate: number;

  @autoserializeAs(RideMetadataValue)
  rideMetadataValues: Array<RideMetadataValue>;

  @autoserialize
  parentRideRequestId: number;

  @autoserialize
  childRideRequestId: number;

  @autoserialize
  riderPhoneNumber: string;

  @autoserialize
  rideBackupEligible: boolean;

  @autoserializeAs(ServiceLevel)
  serviceLevel: ServiceLevel;

  get displayDate(): Date {
    if (this.rideRequestType.onDemand) {
      return this.pickupDate;
    }
    return this.itineraryPickupDate;
  }

  get formattedItineraryDate(): string {
    if (isNullOrUndefined(this.itineraryPickupDate)) {
      return null;
    }
    return moment(this.itineraryPickupDate).format('M/DD/YY');
  }

  get formattedPickupTime(): string {
    if (isNullOrUndefined(this.pickupDate)) {
      return null;
    }
    return moment.tz(this.pickupDate, this.timeZone).format(DateFormatConstants.TIME);
  }

  get formattedDropoffTime(): string {
    if (isNullOrUndefined(this.dropoffDate)) {
      return null;
    }
    return moment.tz(this.dropoffDate, this.timeZone).format(DateFormatConstants.TIME);
  }

  get shortTimeZone(): string {
    return moment.tz(this.pickupDate, this.timeZone).format('z');
  }

  get formattedDisplayDate(): string {
    if (this.rideRequestType.onDemand) {
      return moment(this.itineraryPickupDate).format('M/DD/YY');
    }
    return moment.tz(this.pickupDate, this.timeZone).format('M/DD/YY h:mm a (z)');
  }

  static OnSerialized(instance: RideRequestDetail, json: any): void {
    super.OnSerialized(instance, json);

    if (!isNullOrUndefined(instance.itineraryPickupDate)) {
      json.itineraryPickupDate = DateUtil.serializeLocalDate(instance.itineraryPickupDate);
    }

    if (!isNullOrUndefined(instance.activatedAt)) {
      json.activatedAt = DateUtil.serializeDate(instance.activatedAt);
    }

    if (!isNullOrUndefined(instance.pickupDate)) {
      json.pickupDate = DateUtil.serializeDate(instance.pickupDate);
    }

    if (!isNullOrUndefined(instance.dropoffDate)) {
      json.dropoffDate = DateUtil.serializeDate(instance.dropoffDate);
    }

    if (!isNullOrUndefined(instance.acceptedDate)) {
      json.acceptedDate = DateUtil.serializeDate(instance.acceptedDate);
    }

    if (!isNullOrUndefined(instance.cancelledDate)) {
      json.cancelledDate = DateUtil.serializeDate(instance.cancelledDate);
    }

    if (!isNullOrUndefined(instance.createdDate)) {
      json.createdDate = DateUtil.serializeDate(instance.createdDate);
    }

    if (!isNullOrUndefined(instance.modifiedDate)) {
      json.modifiedDate = DateUtil.serializeDate(instance.modifiedDate);
    }

    if (!isNullOrUndefined(instance.riderNote)) {
      const riderNote: string = instance.riderNote.trim();
      json.riderNote = riderNote.length === 0 ? null : riderNote;
    }
  }

  static OnDeserialized(instance: RideRequestDetail, json: any): void {
    super.OnDeserialized(instance, json);

    if (!isNullOrUndefined(json['itineraryPickupDate'])) {
      instance.itineraryPickupDate = DateUtil.deserializeLocalDate(json['itineraryPickupDate']);
    }

    if (!isNullOrUndefined(json['activatedAt'])) {
      instance.activatedAt = DateUtil.deserializeDate(json['activatedAt']);
    }

    if (!isNullOrUndefined(json['pickupDate'])) {
      instance.pickupDate = DateUtil.deserializeDate(json['pickupDate']);
    }

    if (!isNullOrUndefined(json['dropoffDate'])) {
      instance.dropoffDate = new Date(json['dropoffDate']);
    }

    if (!isNullOrUndefined(json['acceptedDate'])) {
      instance.acceptedDate = DateUtil.deserializeDate(json['acceptedDate']);
    }

    if (!isNullOrUndefined(json['cancelledDate'])) {
      instance.cancelledDate = DateUtil.deserializeDate(json['cancelledDate']);
    }

    if (!isNullOrUndefined(json['createdDate'])) {
      instance.createdDate = DateUtil.deserializeDate(json['createdDate']);
    }

    if (!isNullOrUndefined(json['modifiedDate'])) {
      instance.modifiedDate = DateUtil.deserializeDate(json['modifiedDate']);
    }
  }
}
